import { inject, Pipe, PipeTransform } from '@angular/core';
import { OnlyVisibleActionButtonsService } from '../../action/services/only-visible-action-buttons.service';
import { map, Observable, of, startWith, switchMap } from 'rxjs';
import { isEmptyArray, isNil, Nillable } from '@web/utils';
import { TableRowsSelection } from '../models/table-rows-selection';
import { ActionButton } from '../../action/models/action-button';
import { OnlyVisibleTableCollectionActionsService } from '../services/only-visible-table-collection-actions.service';
import { TableCollectionAction } from '../models/table-collection-action';

@Pipe({
  name: 'tableHeaderVisibility',
  standalone: true,
})
export class TableHeaderVisibilityPipe<
  Item,
  Filter,
  CollectionAction,
  Action,
> implements PipeTransform {
  private readonly onlyVisibleActionButtons = inject<
    OnlyVisibleActionButtonsService<Action>
  >(OnlyVisibleActionButtonsService);
  private readonly onlyVisibleTableCollectionActions = inject<
    OnlyVisibleTableCollectionActionsService<Item, Filter, CollectionAction>
  >(OnlyVisibleTableCollectionActionsService);

  public transform(
    selection$: Observable<Nillable<TableRowsSelection<Item, Filter>>>,
    actions: Nillable<ActionButton<Action>[]>,
    collectionActions: Nillable<
      TableCollectionAction<Item, Filter, CollectionAction>[]
    >,
  ): Observable<boolean> {
    if (isNil(actions) && isNil(collectionActions)) {
      return of(false);
    }

    return selection$.pipe(
      startWith(null),
      map((selection) =>
        this.collectionActionsVisibility(collectionActions!, selection),
      ),
      switchMap((collectionActionsVisibility) => {
        return this.onlyVisibleActionButtons
          .resolveVisibility(actions)!
          .pipe(
            map(
              (actions) =>
                collectionActionsVisibility || !isEmptyArray(actions),
            ),
          );
      }),
    );
  }

  private collectionActionsVisibility(
    collectionActions: TableCollectionAction<Item, Filter, CollectionAction>[],
    selection: TableRowsSelection<Item, Filter> | undefined | null,
  ): boolean {
    const actions = this.onlyVisibleTableCollectionActions.resolveVisibility(
      collectionActions,
      selection,
    );

    return !isNil(actions) && !isEmptyArray(actions);
  }
}
